import { extractTotalCountHeader } from "@mittwald/api-client-NEXT";
import type { MittwaldAPIV2Client } from "@mittwald/api-client-NEXT";
import { assertStatus } from "@mittwald/api-client-commons";
import type { ContributorExtensionBehaviors } from "./types.js";
import { checkForValidationErrors } from "../../../base/index.js";

export const apiContributorExtensionBehaviors = (
  client: MittwaldAPIV2Client,
): ContributorExtensionBehaviors => ({
  find: async (contributorId: string, extensionId: string) => {
    const response = await client.marketplace.extensionGetOwnExtension({
      contributorId,
      extensionId,
    });

    if (response.status === 200) {
      return response.data;
    }

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    assertStatus(response, 404);
  },

  list: async (contributorId, query) => {
    const response = await client.marketplace.extensionListOwnExtensions({
      queryParameters: query,
      contributorId,
    });
    assertStatus(response, 200);
    return {
      items: response.data,
      totalCount: extractTotalCountHeader(response),
    };
  },

  create: async (contributorId, name) => {
    const response = await client.marketplace.extensionRegisterExtension({
      data: {
        name,
      },
      contributorId,
    });
    assertStatus(response, 201);
    return response.data;
  },

  update: async (contributorId, extensionId, data) => {
    const response = await client.marketplace.extensionPatchExtension({
      contributorId,
      extensionId,
      data,
    });

    const validationErrors = checkForValidationErrors(response);
    if (validationErrors) {
      return validationErrors;
    }

    assertStatus(response, 200);
  },

  updatePricing: async (
    contributorId: string,
    extensionId: string,
    price: number,
  ) => {
    const response = await client.axios.put(
      `/v2/contributors/${contributorId}/extensions/${extensionId}/pricing`,
      {
        priceInCents: price,
      },
    );

    const responseBody = response.data;

    if (response.status === 400) {
      const validationErrors = checkForValidationErrors(responseBody);
      if (validationErrors) {
        return validationErrors;
      }
    }

    if (response.status !== 200) {
      throw new Error(
        `Unexpected response status: ${response.status} - ${JSON.stringify(responseBody)}`,
      );
    }

    return responseBody;
  },

  delete: async (contributorId, extensionId) => {
    const response = await client.marketplace.extensionDeleteExtension({
      contributorId,
      extensionId,
    });

    assertStatus(response, 204);
  },

  publish: async (contributorId, extensionId) => {
    const response =
      await client.marketplace.extensionSetExtensionPublishedState({
        data: {
          published: true,
        },
        contributorId,
        extensionId,
      });

    const validationErrors = checkForValidationErrors(response);
    if (validationErrors) {
      return validationErrors;
    }

    assertStatus(response, 200);
  },

  updateContext: async (context, contributorId, extensionId) => {
    const response = await client.marketplace.extensionChangeContext({
      data: { context },
      contributorId,
      extensionId,
    });

    const validationErrors = checkForValidationErrors(response);
    if (validationErrors) {
      return validationErrors;
    }

    assertStatus(response, 200);
  },

  createLogoUploadToken: async (contributorId: string, extensionId: string) => {
    const response = await client.marketplace.extensionRequestLogoUpload({
      contributorId,
      extensionId,
    });
    assertStatus(response, 200);

    return {
      token: response.data.logoRefId,
      rules: response.data.rules,
    };
  },

  deleteExtensionAsset: async (
    contributorId: string,
    extensionId: string,
    assetRefId: string,
  ) => {
    const response = await client.marketplace.extensionRemoveAsset({
      contributorId,
      extensionId,
      assetRefId,
    });

    assertStatus(response, 204);
  },

  createAssetUploadToken: async (
    contributorId: string,
    extensionId: string,
    assetType: "image" | "video",
  ) => {
    const response = await client.marketplace.extensionRequestAssetUpload({
      data: { assetType },
      contributorId,
      extensionId,
    });
    assertStatus(response, 200);

    return {
      token: response.data.assetRefId,
      rules: response.data.rules,
    };
  },

  getPossibleScopes: async () => {
    const response = await client.marketplace.extensionListScopes();
    assertStatus(response, 200);

    return response.data;
  },
});
